import { apiFetch, ApiError } from './api';
import { useAuthStore } from './stores/auth';

export type { WorkspaceRole } from './settings';
import type { WorkspaceRole } from './settings';

export type ProfileDto = {
  id: number;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  /** E.164 (`+12025550123`), joined server-side. Often `null` — captured once
   * at onboarding and never editable since. */
  phoneNumber: string | null;
  timezone: string | null;
  /** UI color scheme. Persisted server-side so it follows the user across
   * devices; applied client-side via `applyTheme` in `lib/theme.ts`. */
  theme: 'light' | 'dark';
  /**
   * The current user's role in their CURRENT workspace, and the source of truth
   * for role gating client-side. `owner` is company-wide — an owner holds it in
   * every workspace — so `role === 'owner'` is the client-side ownership check
   * for billing, org settings and workspace lifecycle (all server-enforced).
   */
  role: WorkspaceRole;
  /**
   * Firebase custom token (uid = the hashed user id the backend writes into
   * Firestore `accessibleUserIds`). Consumed only by `lib/firebase/auth-ready.ts`
   * to sign in to Firebase; short-lived, so it's used straight from a fresh
   * fetch, never from the cache. `null` when the server couldn't mint one.
   */
  firebaseToken: string | null;
};

export type UpdateProfileInput = {
  name?: string;
  timezone?: string;
  theme?: 'light' | 'dark';
};

import { env } from './env';

const BASE = env.apiUrl;

export async function getProfile(): Promise<ProfileDto> {
  return apiFetch<ProfileDto>('/profile');
}

/**
 * The caller's org Warmup Filter ID — the 8-digit number rendered into the
 * signature of every warmup email the org's mailboxes receive. Readable by
 * every role (it lives under `/profile`, not the admin+ `/settings/organization`).
 */
export type WarmupFilterIdDto = { warmupFilterId: string };

export async function getWarmupFilterId(): Promise<WarmupFilterIdDto> {
  return apiFetch<WarmupFilterIdDto>('/profile/warmup-filter-id');
}

export async function updateProfile(input: UpdateProfileInput): Promise<ProfileDto> {
  return apiFetch<ProfileDto>('/profile', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * Mirrors the API's avatar cap (`MAX_SIZE_BYTES` in `s3-avatar.service.ts`).
 * The server enforces it mid-stream regardless; checking here just spares the
 * user uploading a file we already know will be rejected.
 */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Uploads an avatar image as multipart/form-data.
 * apiFetch doesn't support FormData, so we hand-roll fetch here using the same auth/refresh strategy.
 */
export async function uploadAvatar(file: File): Promise<ProfileDto> {
  if (typeof window === 'undefined') {
    throw new ApiError(500, 'uploadAvatar must run in the browser');
  }
  const token = useAuthStore.getState().accessToken;
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${BASE}/profile/avatar`, {
    method: 'POST',
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new ApiError(res.status, body.message ?? 'Avatar upload failed');
  }
  return res.json() as Promise<ProfileDto>;
}
